import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { Hand, Send } from 'lucide-react';
import { climbUrl } from '../../utils/climb-url';
import { climbHeading, climbTitle } from '../../utils/climb-title';
import { useToast } from '../ui/use-toast';
import { makeLogger } from '../../utils/logger';
import { climbShareUrl } from '../climbs/climb-photo-effect';
import type { Climb } from '../../types/climb';

const log = makeLogger('FeedVideoCard');

interface FeedVideoCardProps {
  climb: Climb;
  /** Current user's id (string) for the liked-by-me state, or null when logged out. */
  currentUserId: string | null;
  /** startTime carries the preview's playback position so the viewer resumes instead of restarting. */
  onOpen: (climb: Climb, startTime: number) => void;
  onLike: (climb: Climb) => void;
  onShare: (climb: Climb) => void;
}

/**
 * Feed card showing a climb's latest video (autoplaying when scrolled into view)
 * in place of the cover image, mirroring ClimbInstagramCard's overlay layout but
 * with a like/share footer. Display-only: open/like/share are driven by the grid
 * (which owns the fullscreen viewer, engagement state and cache).
 */
export function FeedVideoCard({ climb, currentUserId, onOpen, onLike, onShare }: FeedVideoCardProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);

  const video = climb.feed_video!;

  // Autoplay only while scrolled into view.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio > 0.5),
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (visible) el.play().catch(() => {});
    else el.pause();
  }, [visible]);

  const likedByMe = !!(currentUserId && video.likers.some(l => l.id === currentUserId));
  // The same heading the rows and the photo cards carry: what the climb is
  // called on the wall, with its given name under it where there is one.
  const { heading, name } = climbHeading(climb);
  const uploadedAgo = formatDistanceToNow(new Date(video.created_at), {
    addSuffix: true,
    locale: i18n.language === 'it' ? it : enUS,
  });
  const uploader = video.user;

  const goToGym = () => {
    const base = climb.location.type === 'Gym' ? 'gyms' : climb.location.type === 'Crag' ? 'crags' : 'competitions';
    navigate(`/${base}/${climb.location.slug}`);
  };

  // Share button: only ever shares the link to this video, never the file itself.
  const handleShareClick = async () => {
    onShare(climb);
    const url = climbShareUrl(`${window.location.origin}${climbUrl(climb)}/video/${video.id}`, climb);
    try {
      if (navigator.share) {
        await navigator.share({ url, title: climbTitle(climb) });
        return;
      }
    } catch {
      /* user cancelled / unsupported — fall through to clipboard */
    }
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: t('climbVideos.linkCopied') });
    } catch {
      /* ignore */
    }
  };

  return (
    <article className="mx-auto w-full max-w-md overflow-hidden rounded-lg border bg-card md:max-w-lg">
      <div className="relative">
        <video
          ref={videoRef}
          src={video.video_url ?? undefined}
          poster={video.poster_url ?? undefined}
          className="aspect-[3/4] w-full cursor-pointer object-cover"
          muted
          loop
          playsInline
          preload="metadata"
          onClick={() => onOpen(climb, videoRef.current?.currentTime ?? 0)}
          onError={() => log.error('feed playback failed', { code: videoRef.current?.error?.code, src: video.video_url })}
        />

        {/* Climb name (top-center) */}
        <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/70 to-transparent px-3 py-2">
          <p className="truncate text-center text-sm font-semibold text-white" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
            {heading}
          </p>
          {name && (
            <p className="truncate text-center text-xs text-white/80" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
              {name}
            </p>
          )}
        </div>

        {/* Gym (bottom-left) */}
        <button
          className="absolute bottom-2 left-2 flex items-center gap-2 rounded-full bg-black/40 px-2 py-1 transition-colors hover:bg-black/60"
          onClick={(e) => { e.stopPropagation(); goToGym(); }}
        >
          {climb.location.logo_small_url && (
            <img src={climb.location.logo_small_url} alt={climb.location.name} className="h-6 w-6 rounded-full border border-white/30 object-cover" />
          )}
          <span className="text-xs font-medium text-white" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{climb.location.name}</span>
        </button>

        {/* Uploader (bottom-right) */}
        <button
          className="absolute bottom-2 right-2 flex items-center gap-2 rounded-full bg-black/40 px-2 py-1 transition-colors hover:bg-black/60"
          onClick={(e) => { e.stopPropagation(); navigate(`/users/${uploader.slug}`); }}
          aria-label={uploader.username}
        >
          <span className="text-xs font-medium text-white" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>{uploader.username}</span>
          {uploader.avatar_small_url ? (
            <img src={uploader.avatar_small_url} alt={uploader.username} className="h-6 w-6 rounded-full border border-white/30 object-cover" />
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-full border border-white/30 bg-muted text-[10px] font-medium">
              {uploader.username.charAt(0).toUpperCase()}
            </div>
          )}
        </button>
      </div>

      {/* Footer: like + count, share + count, upload time (right, muted) */}
      <div className="flex items-center gap-4 px-3 py-3">
        <button
          onClick={() => onLike(climb)}
          aria-label={t('climbVideos.like')}
          className={`flex items-center gap-1.5 text-sm transition-colors ${likedByMe ? 'text-yellow-500' : 'hover:text-primary'}`}
        >
          <Hand className="h-5 w-5" />
          <span>{video.likers.length}</span>
        </button>
        <button
          onClick={handleShareClick}
          aria-label={t('climbVideos.share')}
          className="flex items-center gap-1.5 text-sm transition-colors hover:text-primary"
        >
          <Send className="h-5 w-5" />
          <span>{video.shares_count}</span>
        </button>
        <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">{uploadedAgo}</span>
      </div>
    </article>
  );
}
